import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { Preferences } from "../../profile/objects/preferences";
import { applyColumnResize, ColumnResizePayload } from '../../utils/list/utils/columnResize';
import { EMPTY_SITE, EMPTY_SITE_LIST_CONFIGURATION, EMPTY_SITE_LIST_CRITERIA } from "../constant/siteConstant";
import { Site } from "../objects/site";
import { SiteListCriteria } from "../objects/siteListCriteria";
import { SiteState } from "../objects/siteState";
import { setSitePreference } from "../utils/setSitePreference";
import { Picture } from "../../picture/objects/picture";
import { ADD_CUSTOM_PICTURES, SET_PICTURE } from "../../picture/redux/actionTypes";


const initialState: SiteState = {
    site_list: [],
    site_list_configuration: EMPTY_SITE_LIST_CONFIGURATION,
    site_list_loading: false,
    site_list_count: 0,
    site_list_criteria: EMPTY_SITE_LIST_CRITERIA,
    site: EMPTY_SITE,
    picture: null,
    pictures: [],
    pictures_order: [],
    first_preference_loaded: false,
};


const setPicture = createAction<boolean | Picture>(SET_PICTURE);
const addPictures = createAction<Picture[]>(ADD_CUSTOM_PICTURES);

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const addSiteListConfigurationPreferenceReducer = (state: SiteState, action: PayloadAction<Preferences[]>): SiteState => ({
    ...state, site_list_configuration: { ...state.site_list_configuration, preferences: [...state.site_list_configuration.preferences, ...action.payload] }
});

const setSiteListConfigurationPreferenceReducer = (state: SiteState, action: PayloadAction<Preferences[]>): SiteState => ({
    ...state, site_list_configuration: { ...state.site_list_configuration, preferences: action.payload }
});

const setPreferenceReducer = (state: SiteState, action: PayloadAction<Preferences[]>): SiteState => {
    const { preferences, columns, order_by, order } = setSitePreference(action.payload, state.site_list_configuration.columns);
    return {
        ...state,
        first_preference_loaded: true,
        site_list_configuration: { ...state.site_list_configuration, preferences, columns },
        site_list_criteria: { ...state.site_list_criteria, order, order_by }
    };
};

const moveDownColumnReducer = (state: SiteState, action: PayloadAction<number>): SiteState => {
    const columns = cloneDeep([...state.site_list_configuration.columns]);
    for (let i = 0; i < columns.length; i++) {
        columns[i].last_updated = false;
    }
    const column = { ...columns[action.payload + 1] };
    columns[action.payload + 1] = columns[action.payload];
    columns[action.payload + 1].last_updated = true;
    columns[action.payload] = column;
    return { ...state, site_list_configuration: { ...state.site_list_configuration, columns } };
};


const moveUpColumnReducer = (state: SiteState, action: PayloadAction<number>): SiteState => {
    const columns = cloneDeep([...state.site_list_configuration.columns]);
    for (let i = 0; i < columns.length; i++) {
        columns[i].last_updated = false;
    }
    const column = { ...columns[action.payload - 1] };
    columns[action.payload - 1] = columns[action.payload];
    columns[action.payload - 1].last_updated = true;
    columns[action.payload] = column;
    return { ...state, site_list_configuration: { ...state.site_list_configuration, columns } };
};

const toggleColumnReducer = (state: SiteState, action: PayloadAction<number>): SiteState => {
    const columns = cloneDeep([...state.site_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, site_list_configuration: { ...state.site_list_configuration, columns } };
};

const updateSiteListReducer = (state: SiteState, action: PayloadAction<Site[]>): SiteState => ({
    ...state, site_list: action.payload
});
const updateSiteListCriteriaReducer = (state: SiteState, action: PayloadAction<SiteListCriteria>): SiteState => ({
    ...state, site_list_criteria: action.payload
});
const updateSiteReducer = (state: SiteState, action: PayloadAction<Site>): SiteState => ({
    ...state, site: action.payload,
    pictures: action.payload.pictures as Picture[] || [],
    pictures_order: action.payload && action.payload.pictures !== null
        ? [...action.payload.pictures as Picture[]]
        : [],
});
const updateSiteListLoadingReducer = (state: SiteState, action: PayloadAction<boolean>): SiteState => ({
    ...state, site_list_loading: action.payload
});
const updateSiteListCountReducer = (state: SiteState, action: PayloadAction<number>): SiteState => ({
    ...state, site_list_count: action.payload
});
const setPictureReducer = (
    state: SiteState,
    action: PayloadAction<any>
): SiteState =>
    action.payload !== true && action.payload !== false
        ? {
            ...state,
            pictures: state.pictures.map(
                (picture) =>
                    picture.id === (action.payload as Picture).id
                        ? (action.payload as Picture)
                        : picture
            )
        }
        : { ...state };

const addPicturesReducer = (
    state: SiteState,
    action: PayloadAction<any>
): SiteState => ({
    ...state, pictures: [...state.pictures, ...action.payload]
});

const updatePicturesOrderReducer = (
    state: SiteState,
    action: PayloadAction<{ drag_index: number; hover_index: number }>
): SiteState => ({
    ...state,
    pictures_order: state.pictures_order.map((item, index) => {
        if (index === action.payload.drag_index) {
            return state.pictures_order[action.payload.hover_index];
        } else if (index === action.payload.hover_index) {
            return state.pictures_order[action.payload.drag_index];
        }
        return item;
    })
});


const deletePictureReducer = (state: SiteState): SiteState => ({
    ...state, picture: null
});

const deletePicturesReducer = (
    state: SiteState,
    action: PayloadAction<number>
): SiteState => ({
    ...state,
    pictures: state.pictures.filter(
        (element, index) => index !== action.payload
    )
});

const resizeColumnReducer = (state: SiteState, action: PayloadAction<ColumnResizePayload>): void => {
    state.site_list_configuration.columns = applyColumnResize(state.site_list_configuration.columns, action.payload);
};

export const site = createSlice({
    name: 'site',
    initialState,
    reducers: {
        addSiteListConfigurationPreference: addSiteListConfigurationPreferenceReducer,
        setSiteListConfigurationPreference: setSiteListConfigurationPreferenceReducer,
        moveDownColumn: moveDownColumnReducer,
        moveUpColumn: moveUpColumnReducer,
        toggleColumn: toggleColumnReducer,
        updateSiteList: updateSiteListReducer,
        updateSiteListCriteria: updateSiteListCriteriaReducer,
        updateSite: updateSiteReducer,
        updateSiteListLoading: updateSiteListLoadingReducer,
        updateSiteListCount: updateSiteListCountReducer,
        deletePicture: deletePictureReducer,
        deletePictures: deletePicturesReducer,
        updatePicturesOrder: updatePicturesOrderReducer,
        resizeColumn: resizeColumnReducer
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer)
            .addCase(addPictures, addPicturesReducer)
            .addCase(setPicture, setPictureReducer);
    }
});

export const {
    addSiteListConfigurationPreference,
    setSiteListConfigurationPreference,
    moveDownColumn,
    moveUpColumn,
    toggleColumn,
    updateSiteList,
    updateSiteListCriteria,
    updateSite,
    updateSiteListLoading,
    updateSiteListCount,
    deletePicture,
    deletePictures,
    updatePicturesOrder,
    resizeColumn
} = site.actions;